import { Button, StatusMessage } from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  BulkImportRunCreated,
  useCreateBulkImportMutation,
} from '@/features/bulk-import/bulkImportApi'
import {
  BulkImportRejection,
  getBulkImportRejection,
  getExistingRunId,
} from '@/features/bulk-import/bulkImportErrors'
import {
  BulkImportForm,
  BulkImportFormValues,
} from '@/features/bulk-import/BulkImportForm'
import { BulkImportProblemList } from '@/features/bulk-import/BulkImportProblemList'
import { logger } from '@/utils/logger'

export interface BulkImportPanelProps {
  /**
   * The account to import into, when the surface already knows it. Omitted by the
   * staff surface, which has no account of its own — staff name one in the form, the
   * same way they do when creating a single user.
   */
  accountId?: number
  /**
   * Called once the backend has accepted the file. Defaults to a toast and navigating
   * to the run page. May be async; a rejection is logged like a throw.
   */
  onImportStarted?: (run: BulkImportStartedRun) => void | Promise<void>
  /**
   * Called when the admin asks to see the run an identical earlier upload started.
   * Defaults to navigating to that run's page.
   */
  onViewExistingRun?: (run: BulkImportRunRef) => void
  /** Called when the admin cancels. Defaults to going back one page. */
  onCancel?: () => void
  /**
   * Called when the upload failed in a way the panel cannot show in place, i.e. not
   * a rejected file and not an existing run. Defaults to an error toast.
   */
  onUploadFailed?: (error: unknown) => void
}

export interface BulkImportRunRef {
  accountId: number
  runId: number
}

export interface BulkImportStartedRun extends BulkImportRunRef {
  /** True when these bytes had already been imported and no new run was started. */
  replayed: boolean
}

const bulkImportRunPath = (accountId: number, runId: number): string =>
  `/account/${accountId}/imports/${runId}`

/**
 * The Import tab's contents: the upload form, and what the backend said about the
 * file. Shared by both Create User surfaces so the two cannot come to different
 * conclusions about the same rejection.
 *
 * A refused upload is reported in place rather than as a toast — there can be hundreds
 * of problems and the admin works through them against their own file, which a toast
 * that disappears cannot support.
 */
export const BulkImportPanel = ({
  accountId,
  onImportStarted,
  onViewExistingRun,
  onCancel,
  onUploadFailed,
}: BulkImportPanelProps): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createBulkImport, { isLoading }] = useCreateBulkImportMutation()

  const [rejection, setRejection] = React.useState<
    BulkImportRejection | undefined
  >(undefined)
  const [existingRun, setExistingRun] = React.useState<
    BulkImportRunRef | undefined
  >(undefined)

  const showStartedRun = (run: BulkImportStartedRun): void => {
    // `replayed` means these bytes were already accepted for this account and this
    // request returned that run rather than starting a second import. Saying "import
    // started" would be untrue, and an admin who then waits for a second set of users
    // waits forever.
    toast.success(
      t(run.replayed ? 'BULK_IMPORT_ALREADY_STARTED' : 'BULK_IMPORT_STARTED'),
    )
    navigate(bulkImportRunPath(run.accountId, run.runId))
  }
  const handleImportStarted = onImportStarted ?? showStartedRun
  const handleViewExistingRun =
    onViewExistingRun ??
    ((run: BulkImportRunRef) =>
      navigate(bulkImportRunPath(run.accountId, run.runId)))
  const handleCancel = onCancel ?? (() => navigate(-1))
  const handleUploadFailed =
    onUploadFailed ?? (() => toast.error(t('BULK_IMPORT_UPLOAD_FAILED')))

  const clearOutcome = () => {
    setRejection(undefined)
    setExistingRun(undefined)
  }

  const handleSubmit = async (values: BulkImportFormValues): Promise<void> => {
    // One of the two is always present: the form requires an Account ID whenever the
    // surface did not supply one.
    const target = accountId ?? parseInt(values.accountId, 10)
    if (!values.file || !Number.isFinite(target)) return

    clearOutcome()

    let result: BulkImportRunCreated
    try {
      result = await createBulkImport({
        accountId: target,
        file: values.file,
        suppressInvite: values.suppressInvite,
      }).unwrap()
    } catch (error) {
      const runId = getExistingRunId(error)
      if (runId !== undefined) {
        // The same file sent again asking for something different. The first import
        // exists and cannot be re-decided, so the only useful answer is to point at it.
        setExistingRun({ accountId: target, runId })
        return
      }

      const parsed = getBulkImportRejection(error)
      if (parsed) {
        setRejection(parsed)
      } else {
        // A 5xx or an unreadable body: the backend said nothing worth repeating.
        handleUploadFailed(error)
      }
      logger.error('Error starting bulk import:', error)
      return
    }

    // The import exists by now, so a throwing callback must not read as an upload failure.
    try {
      await handleImportStarted({
        accountId: result.account_id,
        runId: result.id,
        replayed: result.replayed,
      })
    } catch (error) {
      logger.error(
        'Bulk import started, but handling the started run failed:',
        error,
      )
    }
  }

  return (
    <div>
      {existingRun && (
        <StatusMessage
          theme='warning'
          show
          title={t('BULK_IMPORT_ALREADY_SUBMITTED')}
          content={
            <Button
              label={t('BULK_IMPORT_VIEW_EXISTING_RUN')}
              theme='flat'
              dataTestingId='bulk-import-view-existing-run'
              onClick={() => handleViewExistingRun(existingRun)}
            />
          }
        />
      )}
      {rejection && <BulkImportProblemList rejection={rejection} />}
      <BulkImportForm
        showAccountIdField={accountId === undefined}
        isSubmitting={isLoading}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onInputChange={clearOutcome}
        formAriaLabel={t('BULK_IMPORT')}
      />
    </div>
  )
}
